import { faInfoCircle, faLink } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Autocomplete, Avatar, Box, Grid, TextField, Typography } from "@mui/material"
import theme from "css/theme/light"
import { useTranslation } from "react-i18next"
import PaperBox from "components/PaperBox"
import { useEffect } from "react"
import { getFeaturePath } from "./UserForm"
import { StyledPrimaryAvatar } from "shared/styled/Global"
import { StandardTextBox } from "components/StandardTextBox/StandardTextBox"

const reconciliationPeriods = [1, 2, 3, 4, 6, 12]
const reconciliationPeriods2 = [1, 2]
const workingTime = [38.25, 40]

export const BplUserInfo = ({
  values,
  setFieldValue,
  handleBlur,
  handleChange,
  touched,
  errors,
  bplOptionsResult,
  bplUserResult
}) => {
  const { t } = useTranslation()

  useEffect(() => {
    const planningFormikPath = getFeaturePath(values?.modules, "Planning")
    if (bplOptionsResult.status !== 3) return
    setFieldValue(planningFormikPath + ".enabled", false)
  }, [bplOptionsResult])

  useEffect(() => {
    if (bplUserResult.status !== 2) return
    setFieldValue("bplInfo", bplUserResult?.data?.bplInfo)
  }, [bplUserResult])

  const formikManagerIsBplManager =
    values?.manager !== null &&
    bplOptionsResult.status === 2 &&
    values?.bplInfo?.managerId === values?.manager?.bplUserId

  return (
    <PaperBox>
      <Grid container direction="row" spacing={2} justifyContent="space-between" alignItems="center">
        <Grid item xs={2}>
          <Box>
            <StyledPrimaryAvatar aria-label="Sync user till bpl">
              <FontAwesomeIcon icon={faLink} size="lg" />
            </StyledPrimaryAvatar>
          </Box>
        </Grid>
        <Grid item xs={10}>
          <Typography variant="h5" marginBottom={1}>
            {t("settings.form.bpl_integration", "BPL integration")}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2} marginTop={0}>
        {values?.bplUserId === null && (
          <Grid item xs={12} marginBottom={2}>
            <Typography variant="bodySmall" color="textSecondary">
              {t(
                "settings.form.bpl_text",
                "If the user already exists in BPL, the username in the Transport Portal must match the username in BPL. If the username does not match any user in BPL, a new user will be created."
              )}
            </Typography>
          </Grid>
        )}

        {values?.bplUserId !== null && (
          <Grid item xs={12} md={4}>
            <TextField
              autoComplete="off"
              style={{
                width: "100%"
              }}
              disabled
              id="bplUserId"
              label={t(`settings.user.bplUserId`, ``)}
              value={values?.bplUserId}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.bplUserId && errors.bplUserId ? true : false}
              helperText={touched.bplUserId && errors.bplUserId ? errors.bplUserId : null}
              variant="outlined"
            />
          </Grid>
        )}
        <Grid item xs={12} md={8}>
          <Autocomplete
            multiple
            style={{
              width: "100%"
            }}
            disabled={bplOptionsResult.status !== 2}
            componentsProps={{ popper: { style: { width: "240px" } } }}
            id="bplInfo.roles"
            options={bplOptionsResult.status === 2 ? bplOptionsResult?.data?.roles : []}
            getOptionLabel={(option) => (option ? option : "")}
            value={values?.bplInfo?.roles}
            getOptionSelected={(option, value) => {
              return option === value
            }}
            onChange={(e, value) => {
              setFieldValue("bplInfo.roles", value)
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={t(`settings.user.bpl.roles`, ``)}
                name="bplInfo.roles"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.bplInfo?.roles && errors.bplInfo?.roles ? true : false}
                helperText={touched.bplInfo?.roles && errors.bplInfo?.roles ? errors.bplInfo?.roles : null}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            style={{
              width: "100%"
            }}
            disabled={bplOptionsResult.status !== 2}
            componentsProps={{ popper: { style: { width: "240px" } } }}
            id="bplInfo.stationLocationId"
            options={bplOptionsResult.status === 2 && bplOptionsResult?.data?.stations}
            getOptionLabel={(option) => (option.namn ? option.namn : "")}
            value={
              (bplOptionsResult.status === 2 &&
                bplOptionsResult.data.stations.find((i) => i.id === values?.bplInfo?.stationLocationId)) ||
              ""
            }
            getOptionSelected={(option, value) => {
              return option.id === value.id
            }}
            onChange={(e, value) => {
              setFieldValue("bplInfo.stationLocationId", value?.id)
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={t(`settings.user.bpl.stations`, ``)}
                name="bplInfo.stationLocationId"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.bplInfo?.stationLocationId && errors.bplInfo?.stationLocationId ? true : false}
                helperText={
                  touched.bplInfo?.stationLocationId && errors.bplInfo?.stationLocationId
                    ? errors.bplInfo?.stationLocationId
                    : null
                }
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            style={{
              width: "100%"
            }}
            disabled={bplOptionsResult.status !== 2}
            componentsProps={{ popper: { style: { width: "240px" } } }}
            id="bplInfo.employeeCode"
            options={bplOptionsResult.status === 2 && bplOptionsResult?.data?.employeeTypes}
            getOptionLabel={(option) => (option.namn ? option.namn : "")}
            value={
              (bplOptionsResult.status === 2 &&
                bplOptionsResult.data.employeeTypes.find((i) => i.kod === values?.bplInfo?.employeeCode)) ||
              ""
            }
            getOptionSelected={(option, value) => {
              return option.id === value.kod
            }}
            onChange={(e, value) => {
              setFieldValue("bplInfo.employeeCode", value?.kod)
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={t(`settings.user.bpl.employee_types`, ``)}
                name="bplinfo.employeeCode"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.bplInfo?.employeeCode && errors.bplInfo?.employeeCode ? true : false}
                helperText={
                  touched.bplInfo?.employeeCode && errors.bplInfo?.employeeCode
                    ? errors.bplInfo?.employeeCode
                    : null
                }
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            style={{
              width: "100%"
            }}
            disabled={bplOptionsResult.status !== 2}
            componentsProps={{ popper: { style: { width: "240px" } } }}
            id="bplInfo.agreementCode"
            options={bplOptionsResult.status === 2 && bplOptionsResult?.data?.workingAgreements}
            getOptionLabel={(option) => (option.namn ? option.namn : "")}
            value={
              (bplOptionsResult.status === 2 &&
                bplOptionsResult.data.workingAgreements.find(
                  (i) => i.kod === values?.bplInfo?.agreementCode
                )) ||
              ""
            }
            getOptionSelected={(option, value) => {
              return option.id === value.kod
            }}
            onChange={(e, value) => {
              handleChange(e)
              setFieldValue("bplInfo.agreementCode", value?.kod)
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={t(`settings.user.bpl.working_agreement_types`, ``)}
                name="bplInfo.agreementCode"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.bplInfo?.agreementCode && errors.bplInfo?.agreementCode ? true : false}
                helperText={
                  touched.bplInfo?.agreementCode && errors.bplInfo?.agreementCode
                    ? errors.bplInfo?.agreementCode
                    : null
                }
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            style={{
              width: "100%"
            }}
            disabled={bplOptionsResult.status !== 2}
            componentsProps={{ popper: { style: { width: "240px" } } }}
            id="bplInfo.workingScheduleCode"
            options={bplOptionsResult.status === 2 && bplOptionsResult.data.workingSchedule}
            getOptionLabel={(option) => (option.namn ? option.namn : "")}
            value={
              (bplOptionsResult.status === 2 &&
                bplOptionsResult.data.workingSchedule.find(
                  (i) => i.kod === values?.bplInfo?.workingScheduleCode
                )) ||
              ""
            }
            getOptionSelected={(option, value) => {
              return option.id === value.kod
            }}
            onChange={(e, value) => {
              setFieldValue("bplInfo.workingScheduleCode", value?.kod)
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={t(`settings.user.bpl.working_schedule_types`, ``)}
                name="bplInfo.workingScheduleCode"
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched.bplInfo?.workingScheduleCode && errors.bplInfo?.workingScheduleCode ? true : false
                }
                helperText={
                  touched.bplInfo?.workingScheduleCode && errors.bplInfo?.workingScheduleCode
                    ? errors.bplInfo?.workingScheduleCode
                    : null
                }
              />
            )}
          />
        </Grid>
        {values?.bplInfo?.workingScheduleCode === "TIDBANK" && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                autoComplete="off"
                style={{
                  width: "100%"
                }}
                id={"bplInfo.timebankMaxHours"}
                label={t(`settings.user.bpl.timebankMaxHours`, ``)}
                value={values.bplInfo.timebankMaxHours}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.timebankMaxHours && errors.timebankMaxHours ? true : false}
                helperText={
                  touched.timebankMaxHours && errors.timebankMaxHours ? errors.timebankMaxHours : null
                }
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                autoComplete="off"
                style={{
                  width: "100%"
                }}
                id={"bplInfo.timebankMinHours"}
                label={t(`settings.user.bpl.timebankMinHours`, ``)}
                value={values.bplInfo.timebankMinHours}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.timebankMinHours && errors.timebankMinHours ? true : false}
                helperText={
                  touched.timebankMaxHours && errors.timebankMaxHours ? errors.timebankMaxHours : null
                }
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                style={{
                  width: "100%"
                }}
                disabled={bplOptionsResult.status !== 2}
                componentsProps={{ popper: { style: { width: "240px" } } }}
                id="bplInfo.reconciliationPeriodMonth"
                options={reconciliationPeriods}
                getOptionLabel={(option) => option}
                value={
                  reconciliationPeriods.find((i) => i === values?.bplInfo?.reconciliationPeriodMonth) || ""
                }
                getOptionSelected={(option, value) => {
                  return option === value
                }}
                onChange={(e, value) => {
                  setFieldValue("bplInfo.reconciliationPeriodMonth", value)
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label={t(`settings.user.bpl.reconcilation_period_in_months`, ``)}
                    name="reconciliationPeriodMonth"
                  />
                )}
              />
            </Grid>
          </>
        )}
        {values?.bplInfo?.workingScheduleCode === "DAG" && (
          <Grid item xs={12} md={6}>
            <Autocomplete
              style={{
                width: "100%"
              }}
              disabled={bplOptionsResult.status !== 2}
              componentsProps={{ popper: { style: { width: "240px" } } }}
              id="bplInfo.reconciliationPeriodMonth"
              options={reconciliationPeriods2}
              getOptionLabel={(option) => option}
              value={
                reconciliationPeriods2.find((i) => i === values?.bplInfo?.reconciliationPeriodMonth) || ""
              }
              getOptionSelected={(option, value) => {
                return option === value
              }}
              onChange={(e, value) => {
                setFieldValue("bplInfo.reconciliationPeriodMonth", value)
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={t(`settings.user.bpl.reconcilation_period_in_months`, ``)}
                  name="reconciliationPeriodMonth"
                />
              )}
            />
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <Autocomplete
            style={{
              width: "100%"
            }}
            disabled={bplOptionsResult.status !== 2}
            componentsProps={{ popper: { style: { width: "240px" } } }}
            id="bplInfo.workingWeeklyTime"
            options={workingTime}
            getOptionLabel={(option) => option}
            value={workingTime.find((i) => i === values?.bplInfo?.workingWeeklyTime) || ""}
            getOptionSelected={(option, value) => {
              return option === value
            }}
            onChange={(e, value) => {
              setFieldValue("bplInfo.workingWeeklyTime", value)
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={t(`settings.user.bpl.workingWeeklyTime`, ``)}
                name="workingWeeklyTime"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            autoComplete="off"
            style={{
              width: "100%"
            }}
            type="number"
            id={"bplInfo.workingPercentTime"}
            label={t(`settings.user.bpl.workingPercentTime`, ``)}
            value={values?.bplInfo?.workingPercentTime}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.bplInfo?.workingPercentTime && errors.bplInfo?.workingPercentTime ? true : false}
            helperText={
              touched.bplInfo?.workingPercentTime && errors.bplInfo?.workingPercentTime
                ? errors.bplInfo?.workingPercentTime
                : null
            }
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          {!formikManagerIsBplManager && (
            <StandardTextBox
              text={t(
                `settings.user.bpl.missing_match_manager`,
                `Nearest boss does not match against nearest boss in BPL, please select nearest boss in BPL as well`
              )}
              color={theme.palette.default.main}
              bgcolor={theme.palette.primaryLight.main}
              fontSize={14}
              marginTop={0}
            />
          )}
          <Autocomplete
            style={{
              width: "100%"
            }}
            disabled={bplOptionsResult.status !== 2}
            componentsProps={{ popper: { style: { width: "240px" } } }}
            id="bplinfo.managerId"
            options={
              bplOptionsResult.status === 2 &&
              bplOptionsResult.data.managers
                .filter(manager => manager.aktiv === true)
                .sort((a, b) => a.namn.localeCompare(b.namn))
            }
            getOptionLabel={(option) => (option.namn ? option.namn : "")}
            value={bplOptionsResult?.data?.managers
              ?.filter(manager => manager.aktiv === true)
              ?.find((i) => i.id === values?.bplInfo?.managerId) || ""}
            getOptionSelected={(option, value) => {
              return option.id === value.id
            }}
            onChange={(e, value) => {
              setFieldValue("bplInfo.managerId", value?.id)
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={t(`settings.user.manager`, ``)}
                name="managerCode"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.bplInfo?.managerId && errors.bplInfo?.managerId ? true : false}
                helperText={
                  touched.bplInfo?.managerId && errors.bplInfo?.managerId ? errors.bplInfo?.managerId : null
                }
              />
            )}
          />
        </Grid>
      </Grid>
    </PaperBox>
  )
}
